export interface SubmitResult {
  ok: boolean;
  message: string;
}

interface FormResponse {
  code?: string;
  message?: string;
  meta?: {
    message?: string;
    detail?: string;
  };
}

const HONEYPOT_NAMES = ['company_alt', 'contact_alt', 'phone_alt', 'website_alt', 'mobile_alt'];

/**
 * Google Apps Script（ウェブアプリ）のURLをここに貼り付けると、
 * フォーム送信と同時にスプレッドシートへ自動記録されます。
 * 空のままの場合はスプレッドシート連携を行いません。
 */
const SHEET_WEBHOOK_URL = '';

export const sheetSyncEnabled = SHEET_WEBHOOK_URL !== '';

const FIELD_LABELS: Record<string, string> = {
  vendor_name: '出店者名',
  vendor_company: '会社名・屋号',
  vendor_contact: '担当者名',
  vendor_phone: '電話番号',
  vendor_email: 'メールアドレス',
  vendor_line: 'LINE ID',
  vendor_event: '出店希望イベント',
  vendor_date: '希望日程',
  vendor_details: '出店内容・販売商品',
  vendor_style: '出店形態',
  vendor_power: '電源使用',
  vendor_note: '連絡事項',
  contact_category: 'お問い合わせ種別',
  contact_name: 'お名前',
  contact_company: '会社名・屋号',
  contact_phone: '電話番号',
  contact_line: 'LINE ID',
  contact_message: 'お問い合わせ内容',
  email: 'メールアドレス',
};

const formatSubmittedAt = () => {
  const now = new Date();
  const parts = new Intl.DateTimeFormat('ja-JP', {
    timeZone: 'Asia/Tokyo',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).formatToParts(now);
  const pick = (type: string) => parts.find((part) => part.type === type)?.value ?? '';
  return `${pick('year')}/${pick('month')}/${pick('day')} ${pick('hour')}:${pick('minute')}`;
};

async function pushToSheet(kind: string, entries: Array<[string, string]>): Promise<void> {
  if (!sheetSyncEnabled) return;

  const record: Record<string, string> = {
    送信日時: formatSubmittedAt(),
    種別: kind,
    対応状況: '未対応',
  };

  entries.forEach(([key, value]) => {
    record[FIELD_LABELS[key] ?? key] = value;
  });

  try {
    await fetch(SHEET_WEBHOOK_URL, {
      method: 'POST',
      mode: 'no-cors',
      headers: { 'Content-Type': 'text/plain;charset=utf-8' },
      body: JSON.stringify(record),
    });
  } catch {
    // スプレッドシート側の失敗はフォーム送信の成否に影響させない
  }
}

export async function submitFormToEndpoint(
  formEl: HTMLFormElement,
  endpoint: string,
  kind = 'お問い合わせ',
): Promise<SubmitResult> {
  const formData = new FormData(formEl);
  const params = new URLSearchParams();
  const entries: Array<[string, string]> = [];
  let honeypot = '';

  formData.forEach((value, key) => {
    if (typeof value !== 'string') return;
    if (HONEYPOT_NAMES.includes(key)) {
      honeypot = value;
      return;
    }
    if (value.trim() === '') return;
    params.append(key, value);
    entries.push([key, value]);
  });

  if (honeypot.trim() !== '') {
    return { ok: true, message: '' };
  }

  try {
    const response = await fetch(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: params.toString(),
    });

    const responseText = await response.text();

    let parsed: FormResponse | null = null;
    try {
      parsed = JSON.parse(responseText) as FormResponse;
    } catch {
      parsed = null;
    }

    const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
    const isSpam = typeof serverMsg === 'string' && serverMsg.toLowerCase().includes('spam');

    if (!response.ok || !parsed || parsed.code !== 'OK' || isSpam) {
      return {
        ok: false,
        message:
          typeof serverMsg === 'string' && serverMsg.trim() && !isSpam
            ? serverMsg
            : '送信に失敗しました。時間をおいて、もう一度お試しください。',
      };
    }

    await pushToSheet(kind, entries);

    return { ok: true, message: '' };
  } catch {
    return {
      ok: false,
      message: '通信エラーが発生しました。通信環境をご確認のうえ、もう一度お試しください。',
    };
  }
}